import React from 'react';
import styled from 'styled-components';
import { strToFixed } from '../../helpers/utilities';
import { useTranslation } from 'react-i18next';
import { getMarketImageSrc } from '../../helpers/market-images';

const MarketCard = (props) => {
  const { t, i18n } = useTranslation();
  const { to, item, onMarketClick, marginBottom } = props;
  const isComingSoon = !!item.isComingSoon;
  const marketWeight = item.marketWeight || (item.market_resolved === 1 ? 'resolved' : '');
  const isResolved = marketWeight === 'Resolved' || marketWeight === 'resolved' || item.market_resolved === 1;

  const getLabelClassName = (value) => {
    if (value === 'Coming Soon' || value === 'Coming soon' || value === 'coming soon') {
      return 'coming-soon';
    }

    if (value === 'Scalar' || value === 'scalar') {
      return 'scalar';
    }

    if (value === 'Resolved' || value === 'resolved') {
      return 'resolved';
    }

    return null;
  };

  const yesOption = item.options && item.options[0];
  const noOption = item.options && item.options[1];
  const yesPrice = yesOption && !isNaN(yesOption.marginal_price) ? parseFloat(yesOption.marginal_price) : null;
  const noPrice = noOption && !isNaN(noOption.marginal_price) ? parseFloat(noOption.marginal_price) : null;
  const yesPct = yesPrice !== null ? (yesPrice * 100).toFixed(0) : null;
  const noPct = noPrice !== null ? (noPrice * 100).toFixed(0) : null;

  const getOptionLabel = (option, fallbackLabel) => {
    if (!option) {
      return fallbackLabel;
    }

    const translationKey = `markets.${item.id}.options.${option.id}`;
    if (i18n.exists(translationKey)) {
      return t(translationKey);
    }

    return option.option || option.label || option.name || fallbackLabel;
  };

  const yesLabel = getOptionLabel(yesOption, 'Yes');
  const noLabel = getOptionLabel(noOption, 'No');
  const marketImageSrc = isComingSoon ? item.image_url : getMarketImageSrc(item.image_url);

  const handleCardClick = (e) => {
    e.preventDefault();
    onMarketClick(e, to, item);
  };

  const questionText = t(`markets.${item.id}.question`);
  const categoryText = t(`markets.${item.id}.category`);

  return (
    <Card style={{ marginBottom }} onClick={handleCardClick}>
      <CardGlow />

      <CardImage>
        <img src={marketImageSrc} alt={questionText} />
        <ImageOverlay />
      </CardImage>

      <CardBody>
        <CardHeader>
          <TopRow>
            <CategoryBadge>{categoryText}</CategoryBadge>

            {marketWeight && (
              <StatusBadge className={getLabelClassName(marketWeight)}>
                {marketWeight}
              </StatusBadge>
            )}
          </TopRow>

          <Question>{questionText}</Question>
        </CardHeader>

        {!isComingSoon && !isResolved && (yesPct !== null || noPct !== null) && (
          <ProgressBlock>
            <ProgressTrack>
              <ProgressFill style={{ width: `${yesPct || 0}%` }} />
            </ProgressTrack>
            <PriceRow>
              <PriceLabel>
                {yesLabel}
                <PriceValue>{yesPrice !== null ? `$${strToFixed(yesPrice)}` : 'N/A'}</PriceValue>
              </PriceLabel>
              <PriceLabel>
                {noLabel}
                <PriceValue>{noPrice !== null ? `$${strToFixed(noPrice)}` : 'N/A'}</PriceValue>
              </PriceLabel>
            </PriceRow>
          </ProgressBlock>
        )}

        {!isResolved && !isComingSoon && (
          <ButtonsRow>
            <ActionButton
              intent="yes"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onMarketClick(e, to, item);
              }}
            >
              {`Buy ${yesLabel}`}
            </ActionButton>
            <ActionButton
              intent="no"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onMarketClick(e, to, item);
              }}
            >
              {`Buy ${noLabel}`}
            </ActionButton>
          </ButtonsRow>
        )}

        {(isResolved || isComingSoon) && (
          <ResolvedRow>
            <ResolvedText>{isComingSoon ? 'Coming Soon' : 'Market Resolved'}</ResolvedText>
          </ResolvedRow>
        )}
      </CardBody>
    </Card>
  );
};

export default MarketCard;

const CardGlow = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, hsl(263 70% 60%), hsl(190 95% 45%));
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease;
`;

const Card = styled.article`
  position: relative;
  overflow: hidden;
  border: 1px solid hsl(222 47% 15%);
  border-radius: 12px;
  background: linear-gradient(135deg, hsl(222 47% 8% / 0.8), hsl(222 47% 10% / 0.6));
  color: hsl(210 40% 98%);
  backdrop-filter: blur(4px);
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 0 40px hsl(263 70% 60% / 0.3);
  }

  &:hover ${CardGlow} {
    opacity: 0.1;
  }

  &:hover img {
    transform: scale(1.1);
  }
`;

const CardImage = styled.div`
  position: relative;
  width: 100%;
  height: 192px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
`;

const ImageOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 0%, hsl(222 47% 6% / 0.5) 55%, hsl(222 47% 6%) 100%);
  pointer-events: none;
`;

const CardBody = styled.div`
  position: relative;
  padding: 24px;
`;

const CardHeader = styled.div`
  margin-bottom: 16px;
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 16px;
`;

const CategoryBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 999px;
  background: hsl(222 47% 12%);
  color: hsl(215 20% 65%);
  font-size: 12px;
  font-weight: 500;
  line-height: 1.25;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.25;
  white-space: nowrap;

  &.resolved {
    background: hsl(0 84% 60% / 0.2);
    color: hsl(0 84% 60%);
  }

  &.coming-soon {
    background: hsl(142 71% 45% / 0.2);
    color: hsl(142 71% 45%);
  }

  &.scalar {
    background: hsl(190 95% 45% / 0.15);
    color: hsl(190 95% 45%);
  }
`;

const Question = styled.h3`
  margin: 0;
  color: hsl(210 40% 98%);
  font-size: 18px;
  font-weight: 600;
  line-height: 1.25;
`;

const ProgressBlock = styled.div`
  margin-bottom: 16px;
`;

const ProgressTrack = styled.div`
  position: relative;
  width: 100%;
  height: 8px;
  overflow: hidden;
  border-radius: 999px;
  background: hsl(222 47% 12%);
`;

const ProgressFill = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: linear-gradient(90deg, hsl(142 71% 45%), hsl(142 71% 45% / 0.8));
  transition: width 0.5s ease;
`;

const PriceRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-top: 8px;
  color: hsl(215 20% 65%);
  font-size: 12px;
`;

const PriceLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const PriceValue = styled.span`
  color: hsl(210 40% 98%);
  font-size: 14px;
  font-weight: 700;
`;

const ButtonsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 8px 16px;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.25rem;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;

  ${({ intent }) => intent === 'yes' ? `
    background: hsl(142 71% 45% / 0.1);
    color: hsl(142 71% 45%);

    &:hover {
      background: hsl(142 71% 45% / 0.2);
      color: hsl(210 40% 98%);
    }
  ` : `
    background: hsl(0 84% 60% / 0.1);
    color: hsl(0 84% 60%);

    &:hover {
      background: hsl(0 84% 60% / 0.2);
      color: hsl(210 40% 98%);
    }
  `}
`;

const ResolvedRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
`;

const ResolvedText = styled.span`
  color: hsl(215 20% 65%);
  font-size: 14px;
  font-weight: 600;
`;
